import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Ingredient,
  IngredientConversion,
  ingredientsApi,
  Recipe,
  RecipeDetail,
  recipesApi,
  Resource,
  resourcesApi,
  Step,
  stockApi,
  type Variant,
} from "@/client/api";
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  ErrorMessage,
  FormField,
  Input,
  Link,
  PageHeader,
  Select,
  TopLoadingBar,
} from "@/client/components/ui";
import {
  INGREDIENT_TYPE_EMOJIS as TYPE_EMOJIS,
  INGREDIENT_TYPE_LABELS as TYPE_LABELS,
  INGREDIENT_TYPE_ORDER as TYPE_ORDER,
} from "@/client/constants";
import { useDataVersion } from "@/client/context/DataVersionContext";
import { useToast } from "@/client/context/ToastContext";
import { useApi } from "@/client/hooks/useApi";
import { useDragReorder } from "@/client/hooks/useDragReorder";
import { useQueryParam } from "@/client/hooks/useQueryParam";
import { navigate } from "@/client/hooks/useRouter";
import {
  AddIcon,
  BackIcon,
  DeleteIcon,
  DurationIcon,
  EditIcon,
  FreezerIcon,
  IngredientIcon,
  StockIcon,
} from "@/client/icons";
import { formatFraction, formatQty, formatQtyStr, formatRange } from "@/client/utils/formatting";
import {
  humanizeQuantity,
  humanizeSpoon,
  toBaseQuantity,
  UNIT_OPTION_GROUPS,
  UNITS,
  UNIVERSAL_VOLUME,
} from "@/shared/units";

// ─── Step ingredient row (edit mode) ─────────────────────────────────────────

function IngredientRow({
  ing,
  allIngredients,
  onChange,
  onRemove,
}: {
  ing: { ingredient_id: string; quantity: string | null; unit: string | null; optional: boolean };
  allIngredients: Ingredient[];
  onChange: (
    field: "ingredient_id" | "quantity" | "unit" | "optional",
    value: string | null,
  ) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Select
        value={ing.ingredient_id}
        onChange={(v) => onChange("ingredient_id", v)}
        options={allIngredients.map((i) => ({ value: i.id, label: i.name }))}
        placeholder="Ingrediente..."
      />
      <Input
        value={ing.quantity ?? ""}
        onChange={(v) => onChange("quantity", v || null)}
        placeholder="cant."
        className="w-20"
        disabled={!!ing.unit && UNITS[ing.unit]?.category === "descriptive"}
      />
      <select
        value={ing.unit ?? ""}
        onChange={(e) => {
          const v = e.target.value || null;
          onChange("unit", v);
        }}
        className="w-28 px-2 py-2 text-sm bg-term-bg border border-term-border rounded-lg text-term-text focus:border-term-accent-dim focus:outline-none transition-colors appearance-none bg-[length:16px_16px] bg-[position:right_4px_center] bg-no-repeat"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23a08b7a' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
        }}
      >
        <option value="">—</option>
        {UNIT_OPTION_GROUPS.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <label
        className="flex items-center gap-1 text-micro text-term-text-muted cursor-pointer shrink-0"
        title="Opcional"
      >
        <input
          type="checkbox"
          checked={ing.optional}
          onChange={(e) => onChange("optional", e.target.checked ? "1" : null)}
          className="accent-term-accent"
        />
        opc.
      </label>
      <button
        onClick={onRemove}
        className="text-term-text-muted hover:text-term-error cursor-pointer shrink-0"
      >
        <DeleteIcon width={14} height={14} />
      </button>
    </div>
  );
}

// ─── Step resource row (edit mode) ──────────────────────────────────────────

function ResourceRow({
  res,
  allResources,
  onChange,
  onRemove,
  onCreateResource,
}: {
  res: {
    resource_id: string;
    quantity: number;
    temperature_celsius: number | null;
    notes: string | null;
  };
  allResources: Resource[];
  onChange: (field: string, value: unknown) => void;
  onRemove: () => void;
  onCreateResource: (name: string) => Promise<Resource>;
}) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  async function handleCreate() {
    if (!newName.trim()) return;
    const created = await onCreateResource(newName.trim());
    onChange("resource_id", created.id);
    setCreating(false);
    setNewName("");
  }

  if (creating) {
    return (
      <div className="flex items-center gap-2">
        <Input
          value={newName}
          onChange={setNewName}
          placeholder="Nombre del recurso..."
          className="flex-1"
        />
        <Button
          variant="primary"
          size="sm"
          onClick={() => void handleCreate()}
          disabled={!newName.trim()}
        >
          Crear
        </Button>
        <button
          onClick={() => setCreating(false)}
          className="text-term-text-muted hover:text-term-text cursor-pointer text-sm"
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Select
        value={res.resource_id}
        onChange={(v) => {
          if (v === "__new__") {
            setCreating(true);
            return;
          }
          onChange("resource_id", v);
        }}
        options={[
          ...allResources.map((r) => ({ value: r.id, label: r.name })),
          { value: "__new__", label: "+ Nuevo recurso" },
        ]}
        placeholder="Recurso..."
      />
      <Input
        value={res.temperature_celsius != null ? String(res.temperature_celsius) : ""}
        onChange={(v) => onChange("temperature_celsius", v ? parseInt(v) : null)}
        placeholder="°C"
        type="number"
        className="w-20"
      />
      <Input
        value={res.notes ?? ""}
        onChange={(v) => onChange("notes", v || null)}
        placeholder="notas"
        className="w-28"
      />
      <button
        onClick={onRemove}
        className="text-term-text-muted hover:text-term-error cursor-pointer shrink-0"
      >
        <DeleteIcon width={14} height={14} />
      </button>
    </div>
  );
}

// ─── Yield unit select ──────────────────────────────────────────────────────

function YieldUnitSelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const allKnownUnits = UNIT_OPTION_GROUPS.flatMap((g) => g.options.map((o) => o.value));
  const isCustom = value != null && value !== "" && !allKnownUnits.includes(value);
  const [customMode, setCustomMode] = useState(isCustom);
  const [customValue, setCustomValue] = useState(isCustom ? (value ?? "") : "");

  if (customMode) {
    return (
      <div className="flex items-center gap-1.5">
        <Input
          value={customValue}
          onChange={(v) => {
            setCustomValue(v);
            onChange(v || null);
          }}
          placeholder="unidad..."
          className="flex-1"
        />
        <button
          onClick={() => {
            setCustomMode(false);
            setCustomValue("");
            onChange(null);
          }}
          className="text-term-text-muted hover:text-term-text cursor-pointer text-sm shrink-0"
          title="Volver a lista"
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <select
      value={value ?? ""}
      onChange={(e) => {
        if (e.target.value === "__custom__") {
          setCustomMode(true);
          onChange(null);
        } else {
          onChange(e.target.value || null);
        }
      }}
      className="w-full px-2 py-2 text-sm bg-term-bg border border-term-border rounded-lg text-term-text focus:border-term-accent-dim focus:outline-none transition-colors appearance-none bg-[length:16px_16px] bg-[position:right_4px_center] bg-no-repeat"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23a08b7a' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
      }}
    >
      <option value="">—</option>
      {UNIT_OPTION_GROUPS.map((group) => (
        <optgroup key={group.label} label={group.label}>
          {group.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
      ))}
      <option value="__custom__">Otro…</option>
    </select>
  );
}

// ─── Dependency helpers ──────────────────────────────────────────────────────

interface EditableStep extends Omit<Step, "ingredients" | "resources" | "depends_on"> {
  ingredients: {
    ingredient_id: string;
    ingredient_name?: string;
    ingredient_type?: string;
    quantity: string | null;
    unit: string | null;
    optional: boolean;
  }[];
  resources: {
    resource_id: string;
    resource_name?: string;
    quantity: number;
    temperature_celsius: number | null;
    notes: string | null;
  }[];
  depends_on: string[];
}

import { transitiveReachable } from "@/client/computations/recipe";

function DependencyEditor({
  step,
  allSteps,
  onChange,
}: {
  step: EditableStep;
  allSteps: EditableStep[];
  onChange: (updated: EditableStep) => void;
}) {
  const stepPhase = step.phase ?? "meal-prep";
  const candidates = allSteps.filter(
    (s) =>
      s.id !== step.id &&
      (s.phase ?? "meal-prep") === stepPhase &&
      (s.order_num ?? 0) < (step.order_num ?? 0),
  );
  const violations = step.depends_on.filter((depId) => {
    const dep = allSteps.find((s) => s.id === depId);
    if (!dep) return true;
    return (
      (dep.phase ?? "meal-prep") !== stepPhase || (dep.order_num ?? 0) >= (step.order_num ?? 0)
    );
  });
  const reachable = transitiveReachable(step.depends_on, allSteps);

  function toggleDep(id: string) {
    const newDeps = step.depends_on.includes(id)
      ? step.depends_on.filter((d) => d !== id)
      : [...step.depends_on, id];
    onChange({ ...step, depends_on: newDeps });
  }

  if (candidates.length === 0 && violations.length === 0) {
    return (
      <p className="text-sm text-term-text-muted italic">
        Primer paso — sin dependencias posibles.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {violations.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {violations.map((depId) => {
            const dep = allSteps.find((s) => s.id === depId);
            return (
              <button
                key={depId}
                type="button"
                onClick={() =>
                  onChange({ ...step, depends_on: step.depends_on.filter((d) => d !== depId) })
                }
                className="text-sm px-2 py-0.5 rounded-full border bg-term-error/10 border-term-error/30 text-term-error font-medium cursor-pointer transition-all hover:bg-term-error/20"
                title="Dependencia inválida — click para quitar"
              >
                <span className="font-medium">{dep?.order_num ?? "?"}.</span>{" "}
                {(dep?.description ?? depId).slice(0, 30)}
                {(dep?.description ?? "").length > 30 ? "…" : ""} ×
              </button>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {candidates.map((s) => {
          const selected = step.depends_on.includes(s.id);
          const isRedundant = !selected && reachable.has(s.id);
          return (
            <button
              key={s.id}
              type="button"
              disabled={isRedundant}
              title={isRedundant ? "Ya cubierto transitivamente" : undefined}
              onClick={() => {
                if (!isRedundant) toggleDep(s.id);
              }}
              className={`text-sm px-2 py-0.5 rounded-full border transition-all ${
                isRedundant
                  ? "opacity-30 cursor-not-allowed border-term-border text-term-text-muted"
                  : selected
                    ? "bg-term-accent/15 border-term-accent/40 text-term-accent font-medium cursor-pointer"
                    : "bg-term-raised border-term-border text-term-text-muted hover:border-term-border-bright cursor-pointer"
              }`}
            >
              <span className="font-medium">{s.order_num}.</span> {s.description.slice(0, 30)}
              {s.description.length > 30 ? "…" : ""}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step card ─────────────────────────────────────────────────────────────────

function StepCard({
  step,
  allSteps,
  allIngredients,
  allResources,
  allRecipes,
  variants,
  displayNum,
  editMode,
  multiplier,
  onChange,
  onRemove,
  onCreateResource,
  onMove,
  dragHandleProps,
  defaultOpen,
  formatWithAlt,
  onToggleAlt,
}: {
  step: EditableStep;
  allSteps: EditableStep[];
  allIngredients: Ingredient[];
  allResources: Resource[];
  allRecipes?: Recipe[];
  variants?: Variant[];
  displayNum?: number;
  editMode: boolean;
  multiplier: number;
  onChange: (updated: EditableStep) => void;
  onRemove: () => void;
  onCreateResource: (name: string) => Promise<Resource>;
  onMove?: (dir: "up" | "down") => void;
  dragHandleProps?: { onPointerDown: (e: React.PointerEvent) => void; style: React.CSSProperties };
  defaultOpen?: boolean;
  formatWithAlt?: (
    qty: number,
    unit: string,
    ingredientId: string,
    multiplier: number,
  ) => { text: string; canCycle: boolean; repCount: number };
  onToggleAlt?: (ingredientId: string, repCount: number) => void;
}) {
  const cardRef = React.useRef<HTMLDivElement>(null);

  const stepPhase = step.phase ?? "meal-prep";
  const dependencies = step.depends_on.map((depId) => {
    const found = allSteps.find((s) => s.id === depId);
    const depPhase = found?.phase ?? "meal-prep";
    const isViolation =
      !found || depPhase !== stepPhase || (found.order_num ?? 0) >= (step.order_num ?? 0);
    return {
      id: depId,
      num: found?.order_num ?? "?",
      description: found ? found.description : depId,
      isViolation,
    };
  });

  const stepComponents = (step as Step).components ?? [];
  const hasExpandableContent =
    editMode ||
    step.ingredients.length > 0 ||
    dependencies.length > 0 ||
    step.resources.length > 0 ||
    stepComponents.length > 0;
  const [open, setOpen] = useState(!!defaultOpen && hasExpandableContent);

  function handleHeaderKey(e: React.KeyboardEvent) {
    if (!hasExpandableContent) return;
    const tag = (e.target as HTMLElement).tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((o) => !o);
    }
  }

  function handleCardKey(e: React.KeyboardEvent) {
    if (e.key === "Escape" && open) {
      e.stopPropagation();
      setOpen(false);
      // Return focus to the header
      const header = cardRef.current?.querySelector<HTMLElement>("[data-step-header]");
      header?.focus();
    }
  }

  function handleGripKey(e: React.KeyboardEvent) {
    if (!onMove) return;
    if (e.key === "ArrowUp") {
      e.preventDefault();
      onMove("up");
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      onMove("down");
    }
  }

  return (
    <div
      ref={cardRef}
      data-step-id={step.id}
      className={`border rounded-xl bg-term-surface overflow-hidden transition-colors ${editMode ? "border-term-accent/25" : "border-term-border"}`}
      onKeyDown={handleCardKey}
    >
      <div
        data-step-header
        tabIndex={hasExpandableContent ? 0 : undefined}
        role={hasExpandableContent ? "button" : undefined}
        aria-expanded={hasExpandableContent ? open : undefined}
        className={`px-4 py-3 transition-colors ${hasExpandableContent ? "cursor-pointer hover:bg-term-raised/50 focus-visible:bg-term-raised/70" : ""} ${open ? "rounded-t-xl" : "rounded-xl"}`}
        onClick={hasExpandableContent ? () => setOpen((o) => !o) : undefined}
        onKeyDown={handleHeaderKey}
      >
        <div className="flex items-center gap-3">
          {editMode && dragHandleProps && (
            <div
              tabIndex={0}
              role="button"
              aria-label="Mover paso (flechas arriba/abajo)"
              className="shrink-0 text-term-text-muted hover:text-term-text cursor-grab active:cursor-grabbing rounded"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                handleGripKey(e);
              }}
              {...dragHandleProps}
            >
              <span className="text-sm leading-none tracking-[3px]">⠿</span>
            </div>
          )}
          <span
            className={`w-6 h-6 rounded-full border text-xs font-medium flex items-center justify-center shrink-0 ${
              editMode
                ? "bg-term-accent/10 border-term-accent/30 text-term-accent"
                : step.variant_id != null
                  ? "bg-term-accent/10 border-term-accent/30 text-term-accent"
                  : "bg-term-raised border-term-border text-term-text-dim"
            }`}
          >
            {displayNum ?? step.order_num}
          </span>
          {editMode ? (
            <textarea
              ref={(el) => {
                if (el) {
                  el.style.height = "auto";
                  el.style.height = el.scrollHeight + "px";
                }
              }}
              value={step.description}
              onChange={(e) => {
                const t = e.target;
                t.style.height = "auto";
                t.style.height = t.scrollHeight + "px";
                onChange({ ...step, description: t.value });
              }}
              onClick={(e) => e.stopPropagation()}
              rows={1}
              className="flex-1 text-sm text-term-text bg-term-bg/50 px-2 py-1 rounded-lg border border-term-border focus:border-term-accent-dim resize-none overflow-hidden [&]:outline-none!"
            />
          ) : (
            <span className="flex-1 text-sm text-term-text selectable">{step.description}</span>
          )}
          {/* Desktop: inline metadata */}
          <div className="hidden md:flex items-center gap-2 shrink-0 text-sm text-term-text-muted">
            {step.variant_id != null && !editMode && (
              <span
                className="w-[7px] h-[7px] mx-0.5 bg-term-accent rotate-45 shrink-0"
                title={`Solo en: ${variants?.find((v) => v.id === step.variant_id)?.name ?? "variante"}`}
              />
            )}
            {step.variant_id != null && editMode && variants?.length ? (
              <span className="inline-flex items-center text-micro font-medium text-term-accent bg-term-accent/10 px-1.5 py-0.5 rounded-full border border-term-accent/20">
                {variants.find((v) => v.id === step.variant_id)?.name ?? "?"}
              </span>
            ) : null}
            {step.is_passive && (
              <span
                className="inline-flex items-center gap-0.5 text-micro font-medium text-term-accent-dim bg-term-accent/10 px-1.5 py-0.5 rounded-full"
                title="Paso pasivo"
              >
                <DurationIcon width={11} height={11} className="shrink-0" aria-hidden />
                Pasivo
              </span>
            )}
            {step.duration_minutes != null && (
              <span className="inline-flex items-center gap-0.5">
                <DurationIcon width={11} height={11} className="shrink-0 opacity-60" aria-hidden />
                {step.duration_minutes}min
              </span>
            )}
            {step.ingredients.length > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <IngredientIcon
                  width={11}
                  height={11}
                  className="shrink-0 opacity-60"
                  aria-hidden
                />
                {step.ingredients.length} ing.
              </span>
            )}
            {editMode && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="text-term-text-muted hover:text-term-error cursor-pointer ml-1"
              >
                <DeleteIcon width={13} height={13} />
              </button>
            )}
          </div>
          {hasExpandableContent && (
            <span
              className={`text-term-text-muted text-sm transition-transform duration-200 ${open ? "rotate-90" : ""}`}
            >
              ▸
            </span>
          )}
        </div>
        {/* Mobile: metadata below description */}
        {!editMode &&
          (step.duration_minutes != null || step.ingredients.length > 0 || step.is_passive) && (
            <div className="flex md:hidden items-center gap-3 mt-1.5 ml-9 text-xs text-term-text-muted">
              {step.is_passive && (
                <span
                  className="inline-flex items-center gap-0.5 font-medium text-term-accent-dim bg-term-accent/10 px-1.5 py-0.5 rounded-full"
                  title="Paso pasivo"
                >
                  <DurationIcon width={10} height={10} className="shrink-0" aria-hidden />
                  Pasivo
                </span>
              )}
              {step.duration_minutes != null && (
                <span className="inline-flex items-center gap-1">
                  <DurationIcon
                    width={11}
                    height={11}
                    className="shrink-0 opacity-60"
                    aria-hidden
                  />
                  {step.duration_minutes}min
                </span>
              )}
              {step.ingredients.length > 0 && (
                <span className="inline-flex items-center gap-1">
                  <IngredientIcon
                    width={11}
                    height={11}
                    className="shrink-0 opacity-60"
                    aria-hidden
                  />
                  {step.ingredients.length} ing.
                </span>
              )}
            </div>
          )}
        {/* Mobile: edit delete button */}
        {editMode && (
          <div className="flex md:hidden items-center gap-2 mt-1.5 ml-9 text-sm text-term-text-muted">
            {step.duration_minutes != null && (
              <span className="inline-flex items-center gap-1">
                <DurationIcon width={11} height={11} className="shrink-0 opacity-60" aria-hidden />
                {step.duration_minutes}min
              </span>
            )}
            {step.ingredients.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <IngredientIcon
                  width={11}
                  height={11}
                  className="shrink-0 opacity-60"
                  aria-hidden
                />
                {step.ingredients.length} ing.
              </span>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="text-term-text-muted hover:text-term-error cursor-pointer ml-auto"
            >
              <DeleteIcon width={13} height={13} />
            </button>
          </div>
        )}
      </div>

      {open && (
        <div className="px-4 pb-4 border-t border-term-border flex flex-col gap-4 pt-3">
          {editMode && (
            <div className="flex flex-col gap-3 bg-term-bg/50 rounded-lg px-3 py-3">
              <div className={`grid gap-3 ${variants?.length ? "grid-cols-3" : "grid-cols-2"}`}>
                <FormField label="Duración (min)">
                  <Input
                    value={String(step.duration_minutes ?? "")}
                    onChange={(v) =>
                      onChange({ ...step, duration_minutes: v ? parseInt(v) : null })
                    }
                    type="number"
                    placeholder="30"
                  />
                </FormField>
                <FormField label="Fase">
                  <Select
                    value={step.phase ?? ""}
                    onChange={(v) =>
                      onChange({ ...step, phase: (v as "meal-prep" | "day-of-eating") || null })
                    }
                    options={[
                      { value: "meal-prep", label: "Meal prep" },
                      { value: "day-of-eating", label: "Día de" },
                    ]}
                    placeholder="Fase..."
                  />
                </FormField>
                {variants?.length ? (
                  <FormField label="Variante">
                    <Select
                      value={step.variant_id ?? ""}
                      onChange={(v) => onChange({ ...step, variant_id: v || null })}
                      options={[
                        { value: "", label: "Compartido" },
                        ...variants.map((vr) => ({ value: vr.id, label: vr.name })),
                      ]}
                    />
                  </FormField>
                ) : null}
              </div>
              <label className="flex items-start gap-2 cursor-pointer text-sm text-term-text-dim">
                <input
                  type="checkbox"
                  checked={step.is_passive}
                  onChange={(e) => onChange({ ...step, is_passive: e.target.checked })}
                  className="mt-0.5 rounded border-term-border accent-term-accent"
                />
                <span>
                  Paso pasivo (sin atención activa: marinar, horno sin vigilancia, reposar…)
                </span>
              </label>
            </div>
          )}

          {/* Ingredients */}
          {(step.ingredients.length > 0 || editMode) && (
            <div>
              <div className="text-xs font-medium text-term-text-muted uppercase tracking-wide mb-1.5">
                Ingredientes
              </div>
              {editMode ? (
                <div className="flex flex-col gap-2">
                  {step.ingredients.map((ing, i) => (
                    <IngredientRow
                      key={i}
                      ing={ing}
                      allIngredients={allIngredients}
                      onChange={(field, value) => {
                        const updated = [...step.ingredients];
                        const patch: Record<string, unknown> = {
                          [field]: field === "optional" ? value === "1" : value,
                        };
                        // Selecting a descriptive unit clears quantity
                        if (
                          field === "unit" &&
                          typeof value === "string" &&
                          UNITS[value]?.category === "descriptive"
                        ) {
                          patch.quantity = null;
                        }
                        updated[i] = { ...updated[i]!, ...patch };
                        onChange({ ...step, ingredients: updated });
                      }}
                      onRemove={() => {
                        onChange({
                          ...step,
                          ingredients: step.ingredients.filter((_, j) => j !== i),
                        });
                      }}
                    />
                  ))}
                  <button
                    onClick={() =>
                      onChange({
                        ...step,
                        ingredients: [
                          ...step.ingredients,
                          {
                            ingredient_id: allIngredients[0]?.id ?? "",
                            quantity: null,
                            unit: null,
                            optional: false,
                          },
                        ],
                      })
                    }
                    className="text-sm text-term-accent hover:bg-term-accent/10 cursor-pointer text-left px-2 py-1 rounded-md transition-colors w-fit"
                  >
                    + agregar ingrediente
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {step.ingredients.map((ing, i) => {
                    const q = ing.quantity != null ? parseFloat(ing.quantity) : null;
                    const isNumeric = q != null && !isNaN(q);
                    let qtyDisplay: { text: string; canCycle: boolean; repCount: number } | null =
                      null;
                    if (isNumeric && ing.unit && formatWithAlt) {
                      qtyDisplay = formatWithAlt(q, ing.unit, ing.ingredient_id, multiplier);
                    } else if (isNumeric && ing.unit) {
                      const base = toBaseQuantity(q * multiplier, ing.unit);
                      if (base) {
                        const h = humanizeQuantity(base.quantity, base.dimension);
                        qtyDisplay = {
                          text: formatQty(h.quantity, h.unit),
                          canCycle: false,
                          repCount: 1,
                        };
                      } else {
                        qtyDisplay = {
                          text: formatQty(q * multiplier, ing.unit),
                          canCycle: false,
                          repCount: 1,
                        };
                      }
                    } else if (isNumeric) {
                      qtyDisplay = {
                        text: formatFraction(q * multiplier),
                        canCycle: false,
                        repCount: 1,
                      };
                    } else if (ing.unit) {
                      qtyDisplay = { text: ing.unit, canCycle: false, repCount: 1 };
                    }
                    return (
                      <span key={i} className="text-sm">
                        <span className="text-term-text-dim">
                          {ing.ingredient_name ?? ing.ingredient_id}
                        </span>
                        {qtyDisplay && (
                          <>
                            {" "}
                            <span
                              className={`text-term-accent font-medium ${qtyDisplay.canCycle ? "cursor-pointer hover:underline" : ""}`}
                              onClick={
                                qtyDisplay.canCycle && onToggleAlt
                                  ? () => onToggleAlt(ing.ingredient_id, qtyDisplay!.repCount)
                                  : undefined
                              }
                            >
                              {qtyDisplay.text}
                            </span>
                          </>
                        )}
                        {!!ing.optional && (
                          <span className="text-term-text-muted italic"> opc.</span>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step components (base recipes, day-of-eating steps only) */}
          {step.phase === "day-of-eating" && (stepComponents.length > 0 || editMode) && (
            <div>
              <div className="text-xs font-medium text-term-text-muted uppercase tracking-wide mb-1.5">
                Componentes
              </div>
              {editMode ? (
                <div className="flex flex-col gap-2">
                  {stepComponents.map((comp, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex-1">
                        <Select
                          value={comp.recipe_id}
                          onChange={(v) => {
                            const updated = [...stepComponents];
                            const selected = (allRecipes ?? []).find((r) => r.id === v);
                            updated[i] = {
                              ...updated[i]!,
                              recipe_id: v,
                              recipe_name: selected?.name ?? "",
                              recipe_type: selected?.type ?? "",
                            };
                            onChange({ ...step, components: updated } as unknown as EditableStep);
                          }}
                          options={(allRecipes ?? [])
                            .filter((r) => r.type === "base" && r.has_meal_prep_steps)
                            .map((r) => ({ value: r.id, label: r.name }))}
                          placeholder="Receta base..."
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-term-text-muted">×</span>
                        <Input
                          value={String(comp.servings_needed)}
                          onChange={(v) => {
                            const updated = [...stepComponents];
                            updated[i] = { ...updated[i]!, servings_needed: parseFloat(v) || 0 };
                            onChange({ ...step, components: updated } as unknown as EditableStep);
                          }}
                          type="number"
                          className="w-16"
                          placeholder="1"
                        />
                        <span className="text-sm text-term-text-muted">porc.</span>
                      </div>
                      <button
                        onClick={() => {
                          onChange({
                            ...step,
                            components: stepComponents.filter((_, j) => j !== i),
                          } as unknown as EditableStep);
                        }}
                        className="text-term-text-muted hover:text-term-error cursor-pointer text-sm"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() =>
                      onChange({
                        ...step,
                        components: [
                          ...stepComponents,
                          {
                            recipe_id: "",
                            recipe_name: "",
                            recipe_type: "base",
                            servings_needed: 1,
                          },
                        ],
                      } as unknown as EditableStep)
                    }
                    className="text-sm text-term-accent hover:bg-term-accent/10 cursor-pointer text-left px-2 py-1 rounded-md transition-colors w-fit"
                  >
                    + agregar componente
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {stepComponents.map((comp, i) => (
                    <Link key={i} href={`/recetas/${comp.recipe_id}`} className="text-sm group">
                      <span className="text-term-text group-hover:text-term-accent transition-colors">
                        {comp.recipe_name}
                      </span>
                      <span className="text-term-accent font-medium">
                        {" "}
                        × {comp.servings_needed} porc.
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Dependencies */}
          {(dependencies.length > 0 || editMode) && (
            <div>
              <div className="text-xs font-medium text-term-text-muted uppercase tracking-wide mb-1.5">
                Depende de
              </div>
              {editMode ? (
                <DependencyEditor step={step} allSteps={allSteps} onChange={onChange} />
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {dependencies.map((d, i) => (
                    <span
                      key={i}
                      className={`text-sm px-2 py-0.5 rounded-full border truncate max-w-48 ${
                        d.isViolation
                          ? "bg-term-error/10 border-term-error/30 text-term-error"
                          : "bg-term-raised border-term-border text-term-text-dim"
                      }`}
                      title={
                        d.isViolation
                          ? "Dependencia inválida (diferente fase o paso posterior)"
                          : undefined
                      }
                    >
                      <span
                        className={`font-medium ${d.isViolation ? "text-term-error" : "text-term-text-muted"}`}
                      >
                        {d.num}.
                      </span>{" "}
                      {d.description}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Resources */}
          {(step.resources.length > 0 || editMode) && (
            <div>
              <div className="text-xs font-medium text-term-text-muted uppercase tracking-wide mb-1.5">
                Recursos
              </div>
              {editMode ? (
                <div className="flex flex-col gap-2">
                  {step.resources.map((res, i) => (
                    <ResourceRow
                      key={i}
                      res={res}
                      allResources={allResources}
                      onChange={(field, value) => {
                        const updated = [...step.resources];
                        updated[i] = { ...updated[i]!, [field]: value };
                        onChange({ ...step, resources: updated });
                      }}
                      onRemove={() => {
                        onChange({ ...step, resources: step.resources.filter((_, j) => j !== i) });
                      }}
                      onCreateResource={onCreateResource}
                    />
                  ))}
                  <button
                    onClick={() =>
                      onChange({
                        ...step,
                        resources: [
                          ...step.resources,
                          {
                            resource_id: allResources[0]?.id ?? "",
                            resource_name: allResources[0]?.name,
                            quantity: 1,
                            temperature_celsius: null,
                            notes: null,
                          },
                        ],
                      })
                    }
                    className="text-sm text-term-accent hover:bg-term-accent/10 cursor-pointer text-left px-2 py-1 rounded-md transition-colors w-fit"
                  >
                    + agregar recurso
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {step.resources.map((r, i) => (
                    <span key={i} className="text-sm">
                      <span className="text-term-text-dim">{r.resource_name ?? r.resource_id}</span>
                      {r.temperature_celsius != null && (
                        <span className="text-term-accent font-medium">
                          {" "}
                          {r.temperature_celsius}°C
                        </span>
                      )}
                      {r.notes && <span className="text-term-text-muted"> ({r.notes})</span>}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Steps section ─────────────────────────────────────────────────────────────

function StepsSection({
  title,
  phase,
  steps,
  allSteps,
  allIngredients,
  allResources,
  allRecipes,
  variants,
  editMode,
  multiplier,
  onChangeStep,
  onReorderSteps,
  onRemoveStep,
  onAddStep,
  onCreateResource,
  formatWithAlt,
  onToggleAlt,
}: {
  title: string;
  phase: "meal-prep" | "day-of-eating";
  steps: EditableStep[];
  allSteps: EditableStep[];
  allIngredients: Ingredient[];
  allResources: Resource[];
  allRecipes?: Recipe[];
  variants?: Variant[];
  editMode: boolean;
  multiplier: number;
  onChangeStep: (id: string, updated: EditableStep) => void;
  onReorderSteps: (phase: string, reordered: EditableStep[]) => void;
  onRemoveStep: (id: string) => void;
  onAddStep: (phase: "meal-prep" | "day-of-eating") => void;
  onCreateResource: (name: string) => Promise<Resource>;
  formatWithAlt?: (
    qty: number,
    unit: string,
    ingredientId: string,
    multiplier: number,
  ) => { text: string; canCycle: boolean; repCount: number };
  onToggleAlt?: (ingredientId: string, repCount: number) => void;
}) {
  const { dragHandleProps, listRef } = useDragReorder({
    items: steps,
    onReorder: (reordered) => onReorderSteps(phase, reordered),
    getId: (s) => s.id,
    enabled: editMode,
  });

  function moveStep(stepId: string, dir: "up" | "down") {
    const idx = steps.findIndex((s) => s.id === stepId);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= steps.length) return;
    const reordered = [...steps];
    [reordered[idx], reordered[target]] = [reordered[target]!, reordered[idx]!];
    onReorderSteps(phase, reordered);
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif text-lg text-term-text">{title}</h2>
        {editMode && (
          <Button variant="ghost" size="sm" onClick={() => onAddStep(phase)}>
            <AddIcon width={13} height={13} />
            Agregar paso
          </Button>
        )}
      </div>

      {steps.length === 0 ? (
        <p className="text-sm text-term-text-muted italic">Sin pasos.</p>
      ) : (
        <div ref={listRef} className="flex flex-col gap-2">
          {steps.map((step, idx) => (
            <StepCard
              key={step.id}
              step={step}
              allSteps={allSteps}
              allIngredients={allIngredients}
              allResources={allResources}
              allRecipes={allRecipes}
              variants={variants}
              displayNum={editMode ? undefined : idx + 1}
              editMode={editMode}
              multiplier={multiplier}
              onChange={(updated) => onChangeStep(step.id, updated)}
              onRemove={() => onRemoveStep(step.id)}
              onCreateResource={onCreateResource}
              onMove={editMode ? (dir) => moveStep(step.id, dir) : undefined}
              dragHandleProps={editMode ? dragHandleProps(step.id) : undefined}
              defaultOpen={editMode && step.id.startsWith("new_")}
              formatWithAlt={formatWithAlt}
              onToggleAlt={onToggleAlt}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Batch history row (expandable) ───────────────────────────────────────────

function BatchHistoryRow({ batch: b }: { batch: import("../api").BatchStock }) {
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState<
    { id: string; servings_consumed: number; consumption_date: string }[] | null
  >(null);

  async function toggleExpand() {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (!details) {
      const data = await stockApi.batchConsumptions(b.batch_id);
      setDetails(data);
    }
  }

  const expiry = b.storage_location === "fridge" ? b.fridge_expiry : b.freezer_expiry;

  return (
    <>
      <tr className="border-b border-term-border/50 hover:bg-term-raised/30">
        <td className="py-2 pr-2 w-6">
          {b.servings_consumed > 0 && (
            <button
              onClick={toggleExpand}
              className="text-term-text-muted hover:text-term-text cursor-pointer text-sm"
              aria-label="Ver consumos"
            >
              {expanded ? "▾" : "▸"}
            </button>
          )}
        </td>
        <td className="py-2 pr-4 text-term-text">{b.prep_date}</td>
        <td className="py-2 pr-4 text-term-text-dim">{b.servings_produced}</td>
        <td className="py-2 pr-4 text-term-text-dim">{b.servings_consumed}</td>
        <td className="py-2 pr-4 font-medium text-term-text">{b.servings_remaining}</td>
        <td className="py-2 pr-4 text-term-text-dim">
          <span className="inline-flex items-center justify-center w-5">
            {b.storage_location === "freezer" ? (
              <FreezerIcon width={15} height={15} className="text-term-text-muted" aria-hidden />
            ) : (
              <StockIcon width={15} height={15} className="text-term-text-muted" aria-hidden />
            )}
          </span>
        </td>
        <td className="py-2 pr-4 text-term-text-dim">{expiry ?? "—"}</td>
      </tr>
      {expanded && details && details.length > 0 && (
        <tr>
          <td colSpan={7} className="py-0">
            <div className="pl-8 py-2 border-b border-term-border/30">
              <div className="text-xs font-medium text-term-text-muted uppercase tracking-wide mb-1">
                Consumos
              </div>
              {details.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-4 text-sm text-term-text-dim py-0.5"
                >
                  <span>{c.consumption_date}</span>
                  <span>
                    {c.servings_consumed} {c.servings_consumed !== 1 ? "porciones" : "porción"}
                  </span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Variants section ────────────────────────────────────────────────────────

function VariantsSection({
  variants,
  recipeId,
  onAdded,
  onRemoved,
}: {
  variants: Variant[];
  recipeId: string;
  onAdded: () => void;
  onRemoved: () => void;
}) {
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");

  async function handleAdd() {
    const name = newName.trim();
    if (!name) return;
    try {
      await recipesApi.addVariant(recipeId, name);
      setNewName("");
      setAdding(false);
      onAdded();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    }
  }

  async function handleRemove(variantId: string) {
    try {
      await recipesApi.removeVariant(recipeId, variantId);
      onRemoved();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1 px-1 py-1 bg-term-raised/50 rounded-xl border border-term-border w-fit">
      <span className="text-micro font-medium text-term-text-muted uppercase tracking-wide px-3.5">
        Variantes
      </span>
      {variants.map((v) => (
        <span
          key={v.id}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg bg-term-surface border border-term-border text-term-text"
        >
          <span className="w-[7px] h-[7px] rotate-45 shrink-0 bg-term-accent/40" />
          {v.name}
          <button
            onClick={() => void handleRemove(v.id)}
            className="text-term-text-muted hover:text-term-error cursor-pointer text-xs leading-none ml-0.5"
            title="Quitar variante"
          >
            ×
          </button>
        </span>
      ))}
      {adding ? (
        <div className="inline-flex items-center gap-1.5 pl-1">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleAdd();
              if (e.key === "Escape") {
                setAdding(false);
                setNewName("");
              }
            }}
            placeholder="Nombre..."
            className="text-sm px-2 py-1 bg-term-bg border border-term-border rounded-lg text-term-text focus:border-term-accent-dim focus:outline-none w-32"
          />
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleAdd()}
            disabled={!newName.trim()}
          >
            Crear
          </Button>
          <button
            onClick={() => {
              setAdding(false);
              setNewName("");
            }}
            className="text-term-text-muted hover:text-term-text cursor-pointer text-sm"
          >
            ×
          </button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="text-sm text-term-accent hover:bg-term-surface cursor-pointer px-3 py-1.5 rounded-lg transition-colors"
        >
          + agregar
        </button>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const EMPTY_RECIPE: RecipeDetail = {
  id: "",
  name: "",
  type: null,
  servings: null,
  yield_amount: null,
  yield_unit: null,
  fridge_shelf_life_days: null,
  frozen_shelf_life_days: null,
  has_meal_prep_steps: 0,
  steps: [],
  batches: [],
};

export function RecipeDetailPage({
  id,
  isNew,
  startInEditMode,
}: {
  id?: string;
  isNew?: boolean;
  startInEditMode?: boolean;
}) {
  const { dataVersion } = useDataVersion();
  const fetcher = useCallback(() => {
    void dataVersion;
    return id ? recipesApi.get(id) : Promise.resolve(null);
  }, [id, dataVersion]);
  const ingFetcher = useCallback(() => ingredientsApi.list(), []);
  const resFetcher = useCallback(() => resourcesApi.list(), []);
  const recipesFetcher = useCallback(() => {
    void dataVersion;
    return recipesApi.list();
  }, [dataVersion]);
  const {
    data: recipe,
    loading,
    error,
    refetch,
  } = useApi(fetcher as () => Promise<RecipeDetail | null>, [id]);
  const { data: allIngredients } = useApi(ingFetcher, []);
  const { data: allResources, refetch: refetchResources } = useApi(resFetcher, []);
  const { data: allRecipes } = useApi(recipesFetcher, [dataVersion]);

  // Density map for cross-dimension alt display (ml↔g)
  const densityMap = useMemo(() => {
    const m = new Map<string, number>();
    if (allIngredients) {
      for (const ing of allIngredients) {
        if (ing.density_g_per_ml != null) m.set(ing.id, ing.density_g_per_ml);
      }
    }
    return m;
  }, [allIngredients]);

  // Fetch ingredient conversions for alt display
  const [conversionsMap, setConversionsMap] = useState<Record<string, IngredientConversion[]>>({});
  useEffect(() => {
    if (!recipe) return;
    const ingIds = [
      ...new Set(recipe.steps.flatMap((s) => s.ingredients.map((i) => i.ingredient_id))),
    ];
    if (ingIds.length === 0) return;
    ingredientsApi
      .conversionsBulk(ingIds)
      .then(setConversionsMap)
      .catch(() => {});
  }, [recipe]);
  const [altIndex, setAltIndex] = useState<Record<string, number>>({});
  function toggleAlt(ingredientId: string, count: number) {
    setAltIndex((prev) => ({ ...prev, [ingredientId]: ((prev[ingredientId] ?? 0) + 1) % count }));
  }

  /** Format a scaled qty+unit with cycling alt representations. */
  function formatWithAlt(
    qty: number,
    unit: string,
    ingredientId: string,
    multiplier: number,
  ): { text: string; canCycle: boolean; repCount: number } {
    const scaled = qty * multiplier;

    // Build list of display options
    const options: string[] = [];

    // Option 0: primary display (humanized measurable, or humanized spoon, or raw countable)
    const base = toBaseQuantity(scaled, unit);
    const univMl = UNIVERSAL_VOLUME[unit];
    if (base) {
      const h = humanizeQuantity(base.quantity, base.dimension);
      options.push(formatQty(h.quantity, h.unit));
    } else if (univMl) {
      const h = humanizeSpoon(scaled * univMl);
      options.push(formatQty(h.quantity, h.unit));
    } else {
      options.push(formatQty(scaled, unit));
    }

    const convs = conversionsMap[ingredientId];
    const conv = convs?.find((c) => c.from_unit === unit);

    // Per-ingredient conversion (countable → g or ml)
    if (conv) {
      const vMin = scaled * conv.factor_min;
      const vMax = scaled * conv.factor_max;
      options.push(formatRange(vMin, vMax, conv.to_unit));
    }

    // Universal volume (cda/cdta/taza → ml), skip if per-ingredient already targets ml
    if (univMl && !(conv?.to_unit === "ml")) {
      options.push(formatRange(scaled * univMl, scaled * univMl, "ml"));
    }

    // Reverse: if displaying in base unit, offer countable alt
    if (!conv && !univMl) {
      const displayUnit = base ? humanizeQuantity(base.quantity, base.dimension).unit : unit;
      const convForBase =
        displayUnit === "g" || displayUnit === "kg"
          ? convs?.find((c) => c.to_unit === "g")
          : displayUnit === "ml" || displayUnit === "lt"
            ? convs?.find((c) => c.to_unit === "ml")
            : null;
      if (convForBase) {
        const baseVal = base ? base.quantity : scaled;
        options.push(
          formatRange(
            baseVal / convForBase.factor_max,
            baseVal / convForBase.factor_min,
            convForBase.from_unit,
          ),
        );
      }
    }

    // Cross-dimension via density: ml↔g alternative
    const density = densityMap.get(ingredientId);
    if (density) {
      // Determine the base quantity in ml or g
      const baseMl = base?.dimension === "volume" ? base.quantity : univMl ? scaled * univMl : null;
      const baseG = base?.dimension === "mass" ? base.quantity : null;
      if (baseMl != null) {
        // Volume → mass alt
        const h = humanizeQuantity(baseMl * density, "mass");
        options.push(formatQty(h.quantity, h.unit));
      } else if (baseG != null) {
        // Mass → volume alt
        const h = humanizeQuantity(baseG / density, "volume");
        options.push(formatQty(h.quantity, h.unit));
      }
    }

    const idx = altIndex[ingredientId] ?? 0;
    return {
      text: options[idx % options.length]!,
      canCycle: options.length > 1,
      repCount: options.length,
    };
  }

  const { toast } = useToast();
  const [editMode, setEditMode] = useState(!!isNew || !!startInEditMode);
  const [editData, setEditData] = useState<RecipeDetail>(EMPTY_RECIPE);
  const [saving, setSaving] = useState(false);
  const parseServings = useCallback((raw: string) => {
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 1 ? n : null;
  }, []);
  const serializeServings = useCallback((v: number) => String(v), []);
  const [viewServings, setViewServings] = useQueryParam(
    "porciones",
    parseServings,
    serializeServings,
  );
  const parseVariantId = useCallback((raw: string) => (raw ? raw : null), []);
  const serializeVariantId = useCallback((v: string) => v, []);
  const [selectedVariantId, setSelectedVariantId] = useQueryParam(
    "variante",
    parseVariantId,
    serializeVariantId,
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activePhase, setActivePhase] = useState<"meal-prep" | "day-of-eating">("meal-prep");

  // Reset phase tab when recipe changes
  useEffect(() => {
    if (recipe) {
      const hasMealPrep = recipe.steps.some((s) => (s.phase ?? "meal-prep") === "meal-prep");
      const hasDayOf = recipe.steps.some((s) => s.phase === "day-of-eating");
      setActivePhase(hasMealPrep ? "meal-prep" : hasDayOf ? "day-of-eating" : "meal-prep");
    }
  }, [recipe]);
  // When recipe loads, init edit data and clear viewServings (unless URL had ?porciones)
  useEffect(() => {
    if (recipe) {
      setEditData(recipe);
      const p = new URLSearchParams(window.location.search).get("porciones");
      const urlServings = p ? parseInt(p, 10) : NaN;
      if (
        Number.isFinite(urlServings) &&
        urlServings >= 1 &&
        urlServings !== (recipe.servings ?? 1)
      ) {
        setViewServings(urlServings);
      } else {
        setViewServings(null);
      }
      // Auto-select first variant if recipe has variants and none is selected from URL
      if (recipe.variants?.length && !selectedVariantId) {
        setSelectedVariantId(recipe.variants[0]!.id);
      }
    }
  }, [recipe, setViewServings]); // eslint-disable-line react-hooks/exhaustive-deps -- selectedVariantId read is intentionally non-reactive

  if (!isNew && loading) return <TopLoadingBar visible />;
  if (!isNew && error) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!isNew && !recipe) return null;

  const activeRecipe = recipe ?? EMPTY_RECIPE;

  const display = editMode ? editData : activeRecipe;
  const allEditSteps: EditableStep[] = (editMode ? editData.steps : activeRecipe.steps).map(
    (s) => ({
      ...s,
      phase: s.phase as "meal-prep" | "day-of-eating" | null,
      is_passive: Boolean((s as Step).is_passive),
    }),
  );

  // In view mode, filter steps by selected variant (shared + matching variant)
  const editSteps =
    !editMode && selectedVariantId
      ? allEditSteps.filter((s) => s.variant_id == null || s.variant_id === selectedVariantId)
      : allEditSteps;

  const mealPrepSteps = editSteps.filter((s) => s.phase === "meal-prep" || s.phase == null);
  const dayOfSteps = editSteps.filter((s) => s.phase === "day-of-eating");

  function updateStep(stepId: string, updated: EditableStep) {
    setEditData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        steps: prev.steps.map((s) => (s.id === stepId ? updated : s)),
      } as RecipeDetail;
    });
  }

  /** Renumber order_num per phase (each phase starts from 1) */
  function renumberPerPhase(steps: EditableStep[]): EditableStep[] {
    const counters: Record<string, number> = {};
    return steps.map((s) => {
      const phase = s.phase ?? "meal-prep";
      counters[phase] = (counters[phase] ?? 0) + 1;
      return { ...s, order_num: counters[phase] };
    });
  }

  function reorderPhaseSteps(phase: string, reordered: EditableStep[]) {
    setEditData((prev) => {
      if (!prev) return prev;
      const otherSteps = prev.steps.filter((s) => (s.phase ?? "meal-prep") !== phase);
      return { ...prev, steps: renumberPerPhase([...otherSteps, ...reordered]) } as RecipeDetail;
    });
  }

  function removeStep(stepId: string) {
    setEditData((prev) =>
      prev ? ({ ...prev, steps: prev.steps.filter((s) => s.id !== stepId) } as RecipeDetail) : prev,
    );
  }

  function addStep(phase: "meal-prep" | "day-of-eating", variantId?: string | null) {
    const tempId = `new_${Date.now()}`;
    const newStep: EditableStep = {
      id: tempId,
      recipe_id: id ?? "",
      description: "",
      phase,
      order_num:
        (editData?.steps.filter((s) => (s.phase ?? "meal-prep") === phase).length ?? 0) + 1,
      duration_minutes: null,
      is_passive: false,
      variant_id: variantId ?? null,
      ingredients: [],
      resources: [],
      components: [],
      depends_on: [],
    };
    setEditData((prev) =>
      prev ? ({ ...prev, steps: [...prev.steps, newStep] } as RecipeDetail) : prev,
    );
  }

  async function createResource(name: string): Promise<Resource> {
    const created = await resourcesApi.create({ name });
    void refetchResources();
    return created;
  }

  async function saveChanges() {
    setSaving(true);
    setSaveError(null);
    try {
      let recipeId = id;
      if (isNew) {
        const created = await recipesApi.create({
          name: editData.name,
          type: editData.type,
          servings: editData.servings,
          yield_amount: editData.yield_amount,
          yield_unit: editData.yield_unit,
          fridge_shelf_life_days: editData.fridge_shelf_life_days,
          frozen_shelf_life_days: editData.frozen_shelf_life_days,
        });
        recipeId = created.id;
      } else {
        await recipesApi.update(recipeId!, {
          name: editData.name,
          type: editData.type,
          servings: editData.servings,
          yield_amount: editData.yield_amount,
          yield_unit: editData.yield_unit,
          fridge_shelf_life_days: editData.fridge_shelf_life_days,
          frozen_shelf_life_days: editData.frozen_shelf_life_days,
        });
      }
      // Save steps (order_num is per-phase)
      const renumbered = renumberPerPhase(editData.steps);
      const stepsPayload = renumbered.map((s) => ({
        ...(s.id.startsWith("new_") ? {} : { id: s.id }),
        description: s.description,
        phase: s.phase ?? "meal-prep",
        order_num: s.order_num,
        duration_minutes: s.duration_minutes,
        is_passive: s.is_passive,
        variant_id: s.variant_id ?? null,
        depends_on: s.depends_on,
        ingredients: s.ingredients,
        resources: s.resources,
        components: (s as Step).components?.map((c) => ({
          recipe_id: c.recipe_id,
          servings_needed: c.servings_needed,
        })),
      }));
      await recipesApi.updateSteps(recipeId!, stepsPayload as Partial<Step>[]);
      toast(isNew ? "Receta creada" : "Cambios guardados");
      if (isNew) {
        navigate(`/recetas/${recipeId}`, true);
      } else {
        await refetch();
        setEditMode(false);
        if (id) window.history.replaceState(null, "", `/recetas/${id}`);
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error al guardar", "error");
    } finally {
      setSaving(false);
    }
  }

  // Aggregated ingredients by phase and type
  interface RawQty {
    quantity: string | null;
    unit: string | null;
  }
  interface AggIngredient {
    id: string;
    name: string;
    type: string;
    rawByPhase: Map<string, RawQty[]>;
    phases: Set<string>;
  }

  // Use filtered steps (respects variant selection in view mode)
  const stepsForIngredients = editMode ? activeRecipe.steps : editSteps;
  const aggregatedIngredients = stepsForIngredients.reduce<Map<string, AggIngredient>>(
    (acc, step) => {
      const phase = step.phase ?? "meal-prep";
      for (const ing of step.ingredients) {
        const entry = acc.get(ing.ingredient_id) ?? {
          id: ing.ingredient_id,
          name: ing.ingredient_name ?? ing.ingredient_id,
          type: ing.ingredient_type ?? "",
          rawByPhase: new Map(),
          phases: new Set<string>(),
        };
        entry.phases.add(phase);
        if (ing.quantity != null || ing.unit != null) {
          const phaseQtys = entry.rawByPhase.get(phase) ?? [];
          phaseQtys.push({ quantity: ing.quantity, unit: ing.unit });
          entry.rawByPhase.set(phase, phaseQtys);
        }
        acc.set(ing.ingredient_id, entry);
      }
      return acc;
    },
    new Map(),
  );

  interface FormattedQty {
    text: string;
    canCycle: boolean;
    repCount: number;
    ingredientId: string;
  }

  function formatRawQty(raw: RawQty, multiplier: number, ingredientId: string): FormattedQty {
    const q = raw.quantity != null ? parseFloat(raw.quantity) : null;
    if (q != null && !isNaN(q) && raw.unit) {
      const result = formatWithAlt(q, raw.unit, ingredientId, multiplier);
      return { ...result, ingredientId };
    }
    if (q != null && !isNaN(q)) {
      return {
        text: formatFraction(q * multiplier),
        canCycle: false,
        repCount: 1,
        ingredientId,
      };
    }
    const plain = [raw.quantity != null ? formatQtyStr(raw.quantity) : null, raw.unit]
      .filter(Boolean)
      .join("\u00A0");
    return { text: plain, canCycle: false, repCount: 1, ingredientId };
  }

  // Filter by active phase and format quantities with multiplier
  const baseServings = activeRecipe.servings ?? 1;
  const currentServings = viewServings ?? baseServings;
  const mult = editMode ? 1 : currentServings / baseServings;
  const filteredIngredients = Array.from(aggregatedIngredients.values())
    .filter((ing) => ing.phases.has(activePhase))
    .map((ing) => {
      const rawQtys = ing.rawByPhase.get(activePhase) ?? [];
      const quantities = rawQtys.map((r) => formatRawQty(r, mult, ing.id));
      return { ...ing, quantities };
    });

  // Group by type
  type DisplayIngredient = AggIngredient & { quantities: FormattedQty[] };
  const ingredientsByType = new Map<string, DisplayIngredient[]>();
  for (const ing of filteredIngredients) {
    const list = ingredientsByType.get(ing.type) ?? [];
    list.push(ing);
    ingredientsByType.set(ing.type, list);
  }

  // Aggregated components (day-of-eating steps only)
  interface AggComponent {
    recipe_id: string;
    recipe_name: string;
    recipe_type: string;
    totalServings: number;
  }
  const aggregatedComponents = stepsForIngredients
    .filter((s) => s.phase === "day-of-eating")
    .flatMap((s) => s.components ?? [])
    .reduce<Map<string, AggComponent>>((acc, c) => {
      const entry = acc.get(c.recipe_id);
      if (entry) {
        entry.totalServings += c.servings_needed;
      } else {
        acc.set(c.recipe_id, {
          recipe_id: c.recipe_id,
          recipe_name: c.recipe_name,
          recipe_type: c.recipe_type,
          totalServings: c.servings_needed,
        });
      }
      return acc;
    }, new Map());
  const componentsList = Array.from(aggregatedComponents.values());

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto flex flex-col gap-5 md:gap-8">
      <Link href="/recetas" className="flex items-center gap-1 text-sm text-term-accent -mb-6">
        <BackIcon width={16} height={16} />
        Recetas
      </Link>
      <PageHeader
        title={
          editMode ? (
            <input
              value={editData.name}
              onChange={(e) => setEditData((d) => (d ? { ...d, name: e.target.value } : d))}
              className="font-serif text-2xl text-term-text bg-transparent border-b border-term-border focus:border-term-accent-dim focus:outline-none w-full"
            />
          ) : (
            activeRecipe.name
          )
        }
        actions={
          editMode ? (
            <>
              {saveError && <span className="text-sm text-term-error">{saveError}</span>}
              {!isNew && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setEditData(activeRecipe);
                    setEditMode(false);
                    window.history.replaceState(null, "", `/recetas/${id}`);
                  }}
                >
                  Cancelar
                </Button>
              )}
              {isNew && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    window.history.back();
                  }}
                >
                  Cancelar
                </Button>
              )}
              <Button variant="primary" onClick={saveChanges} disabled={saving}>
                {saving ? "Guardando..." : "Guardar cambios"}
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              onClick={() => {
                setEditMode(true);
                if (id) window.history.replaceState(null, "", `/recetas/${id}/editar`);
              }}
            >
              <EditIcon width={14} height={14} />
              Editar
            </Button>
          )
        }
      />

      {/* Header metadata */}
      <Card className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
        {[
          { label: "Tipo", value: display.type ? <Badge value={display.type} /> : "—" },
          {
            label: "Porciones",
            value:
              !editMode && (display.servings ?? 0) > 0 ? (
                <span className="inline-flex items-center border border-term-border rounded text-xs leading-none">
                  <button
                    onClick={() => setViewServings((s) => Math.max(1, (s ?? baseServings) - 1))}
                    className="w-5 h-5 bg-term-raised text-term-text-dim hover:bg-term-border-bright hover:text-term-text cursor-pointer transition-colors flex items-center justify-center rounded-l"
                    aria-label="Menos"
                  >
                    −
                  </button>
                  <span className="w-6 h-5 flex items-center justify-center font-medium text-term-text bg-term-surface border-x border-term-border">
                    {currentServings}
                  </span>
                  <button
                    onClick={() => setViewServings((s) => (s ?? baseServings) + 1)}
                    className="w-5 h-5 bg-term-raised text-term-text-dim hover:bg-term-border-bright hover:text-term-text cursor-pointer transition-colors flex items-center justify-center rounded-r"
                    aria-label="Más"
                  >
                    +
                  </button>
                </span>
              ) : display.servings != null ? (
                String(display.servings)
              ) : (
                "—"
              ),
          },
          {
            label: "Rinde",
            value:
              display.yield_amount != null
                ? display.yield_unit
                  ? formatQty(display.yield_amount * mult, display.yield_unit)
                  : formatFraction(display.yield_amount * mult)
                : "—",
          },
          {
            label: "Heladera",
            value:
              display.fridge_shelf_life_days != null ? `${display.fridge_shelf_life_days}d` : "—",
          },
          {
            label: "Freezer",
            value:
              display.frozen_shelf_life_days != null ? `${display.frozen_shelf_life_days}d` : "—",
          },
          {
            label: "Pasos",
            value: activeRecipe.steps.length > 0 ? String(activeRecipe.steps.length) : "—",
          },
        ].map(({ label, value }) => (
          <div key={label} className="flex flex-col gap-1">
            <span className="text-xs font-medium text-term-text-muted uppercase tracking-wide">
              {label}
            </span>
            <div className="text-sm text-term-text">{value}</div>
          </div>
        ))}
      </Card>

      {/* Edit header fields */}
      {editMode && (
        <Card className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <FormField label="Tipo">
            <Select
              value={editData.type ?? ""}
              onChange={(v) =>
                setEditData((d) =>
                  d ? { ...d, type: (v as "main" | "side" | "base" | "dessert") || null } : d,
                )
              }
              options={[
                { value: "main", label: "Principal" },
                { value: "side", label: "Guarnición" },
                { value: "base", label: "Base" },
                { value: "dessert", label: "Postre" },
              ]}
              placeholder="Tipo..."
            />
          </FormField>
          <FormField label="Porciones">
            <Input
              value={String(editData.servings ?? "")}
              onChange={(v) =>
                setEditData((d) => (d ? { ...d, servings: v ? parseInt(v) : null } : d))
              }
              type="number"
              placeholder="1"
            />
          </FormField>
          <FormField label="Rinde (cantidad)">
            <Input
              value={String(editData.yield_amount ?? "")}
              onChange={(v) =>
                setEditData((d) => (d ? { ...d, yield_amount: v ? parseFloat(v) : null } : d))
              }
              type="number"
              placeholder="500"
            />
          </FormField>
          <FormField label="Unidad de rinde">
            <YieldUnitSelect
              value={editData.yield_unit}
              onChange={(v) => setEditData((d) => (d ? { ...d, yield_unit: v } : d))}
            />
          </FormField>
          <FormField label="Días heladera">
            <Input
              value={String(editData.fridge_shelf_life_days ?? "")}
              onChange={(v) =>
                setEditData((d) =>
                  d ? { ...d, fridge_shelf_life_days: v ? parseInt(v) : null } : d,
                )
              }
              type="number"
              placeholder="5"
            />
          </FormField>
          <FormField label="Días freezer">
            <Input
              value={String(editData.frozen_shelf_life_days ?? "")}
              onChange={(v) =>
                setEditData((d) =>
                  d ? { ...d, frozen_shelf_life_days: v ? parseInt(v) : null } : d,
                )
              }
              type="number"
              placeholder="90"
            />
          </FormField>
        </Card>
      )}

      {/* Variants — edit mode: management UI */}
      {editMode && !isNew && (
        <VariantsSection
          variants={(display as RecipeDetail).variants ?? []}
          recipeId={id ?? ""}
          onAdded={refetch}
          onRemoved={refetch}
        />
      )}

      {/* Variants — view mode: toggle bar */}
      {!editMode && ((display as RecipeDetail).variants?.length ?? 0) > 0 && (
        <div className="flex items-center gap-1 px-1 py-1 bg-term-raised/50 rounded-xl border border-term-border w-fit">
          <span className="text-micro font-medium text-term-text-muted uppercase tracking-wide px-3.5">
            Variantes
          </span>
          {(display as RecipeDetail).variants!.map((v) => {
            const active = selectedVariantId === v.id;
            return (
              <button
                key={v.id}
                onClick={() => setSelectedVariantId(v.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                  active
                    ? "bg-term-accent text-white shadow-sm"
                    : "text-term-text-muted hover:text-term-text hover:bg-term-surface cursor-pointer"
                }`}
              >
                <span
                  className={`w-[7px] h-[7px] rotate-45 shrink-0 ${active ? "bg-white/70" : "bg-term-accent/40"}`}
                />
                {v.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Phase tabs */}
      {(() => {
        const hasMealPrep =
          editSteps.some((s) => (s.phase ?? "meal-prep") === "meal-prep") || editMode;
        const hasDayOf =
          (editSteps.some((s) => s.phase === "day-of-eating") || editMode) &&
          display.type !== "base";
        const tabs = [
          { key: "meal-prep" as const, label: "Meal prep", has: hasMealPrep },
          { key: "day-of-eating" as const, label: "Día de", has: hasDayOf },
        ];
        const activeSteps = activePhase === "meal-prep" ? mealPrepSteps : dayOfSteps;

        return (
          <>
            <div className="flex items-center gap-1">
              {tabs.map(({ key, label, has }) => (
                <button
                  key={key}
                  onClick={() => has && setActivePhase(key)}
                  disabled={!has}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    activePhase === key
                      ? "bg-term-accent text-white"
                      : has
                        ? "text-term-text-muted hover:text-term-text hover:bg-term-raised cursor-pointer"
                        : "text-term-text-muted/40 cursor-not-allowed"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Ingredients for active phase */}
            {filteredIngredients.length > 0 && (
              <section>
                <h2 className="font-serif text-lg text-term-text mb-4">Ingredientes</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 selectable">
                  {TYPE_ORDER.filter((t) => ingredientsByType.has(t)).map((type) => (
                    <div key={type} className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-sm">{TYPE_EMOJIS[type] ?? "📦"}</span>
                        <span className="text-xs font-medium text-term-text-dim uppercase tracking-wide">
                          {TYPE_LABELS[type] ?? type}
                        </span>
                      </div>
                      {ingredientsByType.get(type)!.map((ing) => {
                        const maxReps = Math.max(...ing.quantities.map((q) => q.repCount));
                        const anyCycle = ing.quantities.some((q) => q.canCycle);
                        return (
                          <div key={ing.id} className="flex items-baseline gap-2 pl-6 text-sm">
                            <span className="text-term-text">{ing.name}</span>
                            {ing.quantities.length > 0 && (
                              <span
                                className={`text-sm text-term-text-muted ${anyCycle ? "cursor-pointer hover:underline hover:text-term-accent" : ""}`}
                                onClick={anyCycle ? () => toggleAlt(ing.id, maxReps) : undefined}
                              >
                                {ing.quantities.map((q) => q.text).join(" + ")}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Components for day-of-eating phase */}
            {activePhase === "day-of-eating" && componentsList.length > 0 && (
              <section>
                <h2 className="font-serif text-lg text-term-text mb-4">Componentes</h2>
                <div className="flex flex-col gap-1.5">
                  {componentsList.map((c) => {
                    const scaled = c.totalServings * mult;
                    return (
                      <div key={c.recipe_id} className="flex items-baseline gap-2 text-sm">
                        <Link
                          href={`/recetas/${c.recipe_id}`}
                          className="text-term-accent hover:underline"
                        >
                          {c.recipe_name}
                        </Link>
                        <span className="text-sm text-term-text-muted">
                          {formatFraction(scaled)}&nbsp;porc.
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Steps for active phase */}
            <StepsSection
              title={activePhase === "meal-prep" ? "Pasos" : "Pasos"}
              phase={activePhase}
              steps={activeSteps}
              allSteps={editSteps}
              allIngredients={allIngredients ?? []}
              allResources={allResources ?? []}
              allRecipes={activePhase === "day-of-eating" ? (allRecipes ?? undefined) : undefined}
              variants={(display as RecipeDetail).variants}
              editMode={editMode}
              multiplier={mult}
              onChangeStep={updateStep}
              onReorderSteps={reorderPhaseSteps}
              onRemoveStep={removeStep}
              onAddStep={addStep}
              onCreateResource={createResource}
              formatWithAlt={formatWithAlt}
              onToggleAlt={toggleAlt}
            />
          </>
        );
      })()}

      {activeRecipe.batches.length > 0 && (
        <section>
          <h2 className="font-serif text-lg text-term-text mb-3">Historial de lotes</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-term-border">
                  {["", "Fecha", "Producido", "Consumido", "Restante", "Almac.", "Vence"].map(
                    (h) => (
                      <th
                        key={h}
                        className="text-left py-2 pr-4 text-xs font-medium text-term-text-muted uppercase tracking-wide"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {activeRecipe.batches.map((b) => (
                  <BatchHistoryRow key={b.batch_id} batch={b} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Delete recipe */}
      {!isNew && !editMode && (
        <div className="pt-2 border-t border-term-border">
          <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
            <DeleteIcon width={13} height={13} />
            Borrar receta
          </Button>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          message={`¿Borrar «${activeRecipe.name}»? Se eliminarán todos sus pasos, lotes y consumos. Esta acción no se puede deshacer.`}
          confirmLabel="Borrar receta"
          danger
          onConfirm={async () => {
            try {
              await recipesApi.delete(id!);
              toast("Receta eliminada");
              navigate("/recetas", true);
            } catch (e) {
              toast(e instanceof Error ? e.message : "Error al eliminar", "error");
              setConfirmDelete(false);
            }
          }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
